import * as React from 'react'
import { Checkbox, Colors, FormGroup, Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import { useRef, useEffect, useState } from 'react'
import isEmpty from 'lodash.isempty'
import Help from '../../../../../components/Help'
import ConfigureOptions from '../ConfigureOptions'
import { UprightStyle } from '../../../../../types'
import { InputLabel } from '../../../../../components/inputs/InputLabel'
import { templateStyles } from './templateStyles'

import './CheckboxGroup.scss'

interface ConfigurationProps {
  input: any
  onChange: (inputPropKey, value) => void
}

interface FormProps {
  input: any
  onFormPreviewChange: (formIndex, value) => void
  index: number
}

const styles: UprightStyle = {
  iconContainer: {
    borderRadius: '50%',
    position: 'absolute',
    bottom: 2,
    right: '50%',
    zIndex: 1,
    background: 'rgb(255,255,255, 0.6)'
  },
  iconStyle: { color: Colors.GRAY2 },
  label: { marginBottom: 0 }
}

const sortValuesAccordingToOriginalAsJS = (options: string[], selected: Set<string>) => {
  const out: string[] = []
  options.forEach(val => {
    if (selected.has(val)) out.push(val)
  })
  return out
}

export const Form: React.FC<FormProps> = ({ input, onFormPreviewChange, index }) => {
  const { props } = input
  const valueSet = new Set<string>(props.value || [])

  const [contentOverflowing, setContentOverflowing] = useState(false)
  const [bottomReached, setBottomReached] = useState(false)

  const checkboxGroupDivRef = useRef<HTMLDivElement>()

  let options = (props.options || []).map((optsHash: any) => optsHash.key)

  if (!props.preserveOrder) {
    options = options.sort((a: string, b: string) => a.localeCompare(b))
  }

  useEffect(() => {
    setContentOverflowing(checkboxGroupDivRef?.current?.clientHeight !== checkboxGroupDivRef?.current?.scrollHeight)
  }, [checkboxGroupDivRef?.current?.scrollHeight, checkboxGroupDivRef?.current?.clientHeight, options?.length])

  return (
    <div
      ref={checkboxGroupDivRef}
      className="checkbox-group-scroll-container"
      onScroll={e => {
        const el = e.currentTarget
        if (el.scrollHeight - el.scrollTop === el.clientHeight) {
          setBottomReached(true)
        } else {
          setBottomReached(false)
        }
      }}
    >
      {contentOverflowing && (
        <div style={styles.iconContainer}>
          <Icon
            icon={IconNames.DOWNLOAD}
            style={{ ...styles.iconStyle, transform: bottomReached ? 'rotate(180deg)' : '' }}
          />
        </div>
      )}
      <InputLabel
        title={props.label}
        required={props.required && isEmpty(props.value)}
        requiredLabelStyle={templateStyles.requiredLabel}
        requiredLabelIconName={props.requiredLabelIconName}
        style={styles.label}
      />
      <>
        <FormGroup>
          {options.map((opt: string, i: number) => {
            return (
              <Checkbox
                // Using an array index should be ok since we re-instantiate the whole component any time these options change
                key={i} // eslint-disable-line react/no-array-index-key
                onChange={v => {
                  if (v.currentTarget.checked === true) {
                    const newValueSet = new Set(valueSet)
                    newValueSet.add(opt)
                    onFormPreviewChange(index, sortValuesAccordingToOriginalAsJS(options, newValueSet))
                  } else {
                    const newValueSet = new Set(Array.from(valueSet).filter(val => val !== opt))
                    onFormPreviewChange(index, sortValuesAccordingToOriginalAsJS(options, newValueSet))
                  }
                }}
                label={opt}
                checked={valueSet.has(opt)}
              />
            )
          })}
        </FormGroup>
        {props.helpText && (
          <Help>
            <div>{props.helpText}</div>
          </Help>
        )}
      </>
    </div>
  )
}

export const Configuration: React.FC<ConfigurationProps> = ({ input, onChange }) => {
  return <ConfigureOptions input={input} onChange={onChange} />
}
